/**
 * Billing Overview Modal - View all tenant usage for billing
 * Master admin only
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, Users, Tag, Eye, Building2, ChevronRight, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BillingOverviewModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/billing/overview`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Billing data is only accessible to master admin');
      } else {
        toast.error('Failed to load billing data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTenantDetails = async (tenantId) => {
    if (tenantId === 'master') return; // Skip master details
    try {
      setLoadingDetails(true);
      setSelectedTenant(tenantId);
      const response = await axios.get(`${API}/api/admin/billing/tenant/${tenantId}`);
      setTenantDetails(response.data);
    } catch (error) {
      toast.error('Failed to load tenant details');
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadBillingData();
      setSelectedTenant(null);
      setTenantDetails(null);
    }
  }, [isOpen, loadBillingData]);

  const exportCSV = () => {
    if (!data) return;
    
    const headers = ['Tenant ID', 'Display Name', 'Domain', 'Total Tags', 'Tags with Content', 'Total Views', 'Status'];
    const rows = data.tenants.map(t => [
      t.tenant_id,
      t.display_name,
      t.domain,
      t.stats.total_tags,
      t.stats.tags_with_content,
      t.stats.total_views,
      t.is_active ? 'Active' : 'Inactive'
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-overview-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success('Billing data exported');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-primary" />
              <div>
                <h2 className="text-xl font-semibold">Billing Overview</h2>
                <p className="text-sm text-muted-foreground">View tenant usage for billing</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadBillingData}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={exportCSV}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
                title="Export CSV"
                disabled={!data}
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : data ? (
              <div className="space-y-6">
                {/* Totals Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">Tenants</span>
                    </div>
                    <p className="text-2xl font-bold">{data.totals.total_tenants}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Tag className="w-4 h-4" />
                      <span className="text-sm">Total Tags</span>
                    </div>
                    <p className="text-2xl font-bold">{data.totals.total_tags_all}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">Total Views</span>
                    </div>
                    <p className="text-2xl font-bold">{data.totals.total_views_all.toLocaleString()}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-secondary/30 border border-border">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Building2 className="w-4 h-4" />
                      <span className="text-sm">Active</span>
                    </div>
                    <p className="text-2xl font-bold">{data.totals.active_tenants}</p>
                  </div>
                </div>

                {/* Tenant List */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Tenant Usage</h3>
                  <div className="space-y-3">
                    {data.tenants.map((tenant) => (
                      <div
                        key={tenant.tenant_id}
                        className={`p-4 rounded-xl border transition-all cursor-pointer ${
                          selectedTenant === tenant.tenant_id
                            ? 'bg-primary/5 border-primary'
                            : 'bg-secondary/20 border-border hover:bg-secondary/40'
                        }`}
                        onClick={() => loadTenantDetails(tenant.tenant_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              tenant.tenant_id === 'master' ? 'bg-primary/20' : 'bg-secondary'
                            }`}>
                              {tenant.tenant_id === 'master' ? (
                                <DollarSign className="w-5 h-5 text-primary" />
                              ) : (
                                <Building2 className="w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{tenant.display_name}</p>
                              <p className="text-sm text-muted-foreground">{tenant.domain}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Tags</p>
                              <p className="font-semibold">{tenant.stats.total_tags}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Views</p>
                              <p className="font-semibold">{tenant.stats.total_views.toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">With Content</p>
                              <p className="font-semibold">{tenant.stats.tags_with_content}</p>
                            </div>
                            {tenant.tenant_id !== 'master' && (
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {selectedTenant === tenant.tenant_id && tenantDetails && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-4 pt-4 border-t border-border"
                          >
                            {loadingDetails ? (
                              <div className="flex justify-center py-4">
                                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Last 7 Days</p>
                                  <p className="font-semibold">{tenantDetails.stats.views_last_7_days} views</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Last 30 Days</p>
                                  <p className="font-semibold">{tenantDetails.stats.views_last_30_days} views</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Unique Visitors</p>
                                  <p className="font-semibold">{tenantDetails.stats.unique_visitors}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Media Files</p>
                                  <p className="font-semibold">{tenantDetails.stats.total_media_files}</p>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Failed to load billing data
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BillingOverviewModal;
